import React, {useState, useEffect, useRef} from 'react';
import { Typography } from '@material-ui/core';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import moment from 'moment';
import 'moment/locale/th';

HighchartsExport(Highcharts);
HighchartsExportData(Highcharts);

const RRIStationChart = React.forwardRef(function({ stationName, type, stationId, damId, isMedium, chartTitle, chartSubtitle, reservoirUnit }, ref) {
  const [chartData, setChartData] = React.useState([]);
  const [chartLoading, setChartLoading] = React.useState(false);
  const [thresholds, setThresholds] = useState(null);
  const [observedData, setObservedData] = useState([]);

  useEffect(() => {
    if (!stationName) return;
    setObservedData([]);

    const today = moment();
    const startDate = today.clone().subtract(7, 'day').format('YYYY-MM-DD');
    const endDate = today.format('YYYY-MM-DD HH:mm');
    const sevenDaysAgo = today.clone().subtract(7, 'day').startOf('day').add(7, 'hours').valueOf();

    if (type === 'station') {
      if (!stationId) return;

      fetch(`${process.env.REACT_APP_API_URL}/public/waterlevel_graph?station_type=tele_waterlevel&station_id=${stationId}&start_date=${startDate}&end_date=${endDate}`)
        .then(res => res.json())
        .then(data => {
          const rows = data?.data?.graph_data ?? [];
          const parsed = rows
            .filter(r => r.discharge !== null)
            .map(r => [moment(r.datetime, 'YYYY-MM-DD HH:mm').valueOf(), r.discharge]);
          setObservedData(parsed);
        })
        .catch(() => {});

    } else if (type === 'reservoir') {
      if (!damId) return;
      const thisYear = today.year();
      if (isMedium) {
        fetch(`${process.env.REACT_APP_API_URL}/analyst/dam_medium_graph?data_type=mediumdam_inflow&medium_id=${damId}&year=${thisYear}`)
          .then(res => res.json())
          .then(data => {
            const rows = data?.data?.graph_data?.[0]?.data ?? [];
            const parsed = rows
              .filter(r => r.value !== null)
              .map(r => [moment.parseZone(r.date).startOf('day').add(7, 'hours').valueOf(), r.value])
              .filter(([ts]) => ts >= sevenDaysAgo)
              .reverse();
            setObservedData(parsed);
          })
          .catch(() => {});
      } else {
        fetch(`${process.env.REACT_APP_API_URL}/analyst/dam_yearly_graph?data_type=dam_inflow&dam_id=${damId}&year=${thisYear}`)
          .then(res => res.json())
          .then(data => {
            const rows = data?.data?.graph_data?.[0]?.data ?? [];
            const parsed = rows
              .filter(r => r.value !== null)
              .map(r => [moment.parseZone(r.date).startOf('day').add(7, 'hours').valueOf(), r.value])
              .filter(([ts]) => ts >= sevenDaysAgo);
            setObservedData(parsed);
          })
          .catch(() => {});
      }
    }
  }, [stationName, type]);
  
  React.useEffect(() => {
    if (!stationName) return;
    const url = `${process.env.REACT_APP_LIVE_HII}/product/latest/rri/station/${stationName}.csv`;
    setChartLoading(true);

    fetch(url)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const parsed = lines.slice(1)
          .map(line => {
            const cols = line.split(',');
            const ts = moment(cols[0], 'YYYY-MM-DD HH:mm:ss').valueOf();
            return {
              ts,
              hr: parseFloat(cols[1]),
              qr: parseFloat(cols[2]),
              qrMCM: parseFloat(cols[3]),
            };
          })
          .filter(d => !isNaN(d.ts));
        setChartData(parsed);
      })
      .catch(() => setChartData([]))
      .finally(() => setChartLoading(false));
  }, [stationName]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_LIVE_HII}/product/latest/rri/metadata/csv/rri_station_coordinate_qr.csv`)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const found = lines.slice(1).find(line => {
          const cols = line.split(',');
          return cols[1]?.trim() === stationName;
        });
        if (found) {
          const cols = found.split(',');
          setThresholds({
            lowest:   parseFloat(cols[4]),
            alarm:    parseFloat(cols[5]),
            warning:  parseFloat(cols[6]),
            critical: parseFloat(cols[7]),
          });
        }
      })
      .catch(() => {});
  }, [stationName]);

  useEffect(() => {
    const chart = ref?.current?.chart;
    if (chart) {
      setTimeout(() => chart.reflow(), 100);
    }
  }, [chartData]);

  if (chartLoading) {
    return (
      <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, color: '#7E8F9F', textAlign: 'center', padding: 16 }}>
        กำลังโหลดข้อมูล...
      </Typography>
    );
  }

  if (chartData.length === 0) {
    return (
      <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, color: '#7E8F9F', textAlign: 'center', padding: 16 }}>
        ไม่มีข้อมูล
      </Typography>
    );
  }

  const isReservoir = type === 'reservoir';
  const isCmsMode = isReservoir && reservoirUnit === 'cms';
  const seriesData = chartData.map(d => [d.ts, isReservoir && !isCmsMode ? d.qrMCM : d.qr]);
  const observedName = isReservoir ? 'ค่าตรวจวัด (MCM)' : 'ค่าตรวจวัด (m³/s)';
  const seriesName = isReservoir ? 'ค่าคาดการณ์ปริมาณน้ำไหลลงอ่าง' : 'ค่าคาดการณ์ปริมาณน้ำท่า';
  const unit = isReservoir && !isCmsMode ? 'ล้าน ลบ.ม. / วัน' : 'ลบ.ม / วินาที';
  const futureColor = '#31985F';
  const futureFill = '#ECF5F0';
  const MCM_TO_CMS = 1000000 / 86400;

  const observedSeriesData = observedData.map(([ts, v]) => [ts, isCmsMode ? v * MCM_TO_CMS : v]);
  // เส้นแบ่ง ณ เวลาปัจจุบัน
  const nowTs = moment().valueOf();
  const lastObserved = observedData.length > 0 ? observedData[observedData.length - 1] : null;
  const splitTs = lastObserved ? lastObserved[0] : nowTs;
  const forecastStartTs = observedData.length > 0 ? observedData[0][0] : splitTs;
  const ToF = moment().startOf('day').add(7, 'hours').valueOf();
  const forecastData = seriesData.filter(([ts]) => ts >= ToF);

  const toDaily = (rows) =>
    rows.filter(([ts, val]) => {
      if (val == null || isNaN(val)) return false;
      return moment(ts).hour() === 7 && moment(ts).minute() === 0;
    });

  const forecastSeriesData = (isReservoir && !isCmsMode ? toDaily(forecastData) : forecastData)
    .map(([ts, v]) => [ts, v]);

  const dataMax = Math.max(
    0,
    ...observedSeriesData.map(([, v]) => v).filter(Number.isFinite),
    ...forecastSeriesData.map(([, v]) => v).filter(Number.isFinite),
  );
  const thresholdMax = thresholds
    ? Math.max(
        0,
        ...[thresholds.lowest, thresholds.alarm, thresholds.warning, thresholds.critical]
          .filter(Number.isFinite),
      )
    : 0;
  const yMax = Math.max(dataMax, thresholdMax) * 1.1;

  const options = {
    time: { useUTC: false, },
    title: {
      text: chartTitle,
      align: 'center',
      style: { fontFamily: 'Noto Sans Thai', fontSize: '12px', fontWeight: 'bold', color: '#2C3E50' },
    },
    subtitle: {
      text: chartSubtitle,
      align: 'center',
      style: { fontFamily: 'Noto Sans Thai', fontSize: '10px', color: '#7E8F9F' },
    },
    chart: {
    zoomType: 'x',
    height: 450,
    marginTop: 60,
    // marginLeft: 60,
    marginRight: 15,
    spacingLeft: 0,
    spacingRight: 0,
    resetZoomButton: {
        theme: { display: 'none' },
    },
    style: { fontFamily: 'Noto Sans Thai' },
    },
    plotOptions: {
      line: { turboThreshold: 0 },
      series: {
        showInNavigator: true,
        gapSize: 6,
      },
    },
    scrollbar: { enabled: true },
    navigator: {
        enabled: true,
        // adaptToUpdatedData: false,
        xAxis: {
            tickInterval: 24 * 3600 * 1000,
            labels: {
            formatter() {
                return moment(this.value).locale('th').format('DD MMM');
            },
            style: { fontFamily: 'Noto Sans Thai', fontSize: '10px' },
            },
        },
    },
    xAxis: {
      type: 'datetime',
      tickInterval: 24 * 3600 * 1000,
      units: [['day', [1]]],
      labels: {
        formatter() {
          return moment(this.value).locale('th').format('DD MMM');
        },
        style: { fontFamily: 'Noto Sans Thai', fontSize: '11px' },
      },
      plotLines: [
        {
          color: '#000000',
          width: 2,
          value: ToF,
          zIndex: 5,
          label: {
            text: 'Time of Forecast',
            style: { fontFamily: 'Noto Sans Thai', fontSize: '11px' },
          },
        },
      ],
    },
    yAxis: {
      opposite: false,
      min: 0,
      max: yMax || undefined,
      endOnTick: false,
      title: {
        text: isReservoir
          ? `ปริมาณน้ำไหลลงอ่าง (${isCmsMode ? 'ลบ.ม./ วินาที' : 'ล้านลบ.ม. / วัน'})`
          : 'ปริมาณน้ำท่า (ลบ.ม. / วินาที)',
        style: { fontFamily: 'Noto Sans Thai', fontSize: '11px', color: '#7E8F9F' },
      },
      labels: {
        // format: '{value:.2f}',
        formatter() {
          return Highcharts.numberFormat(this.value, 2, '.', ',');
        },
        style: { fontFamily: 'Noto Sans Thai' },
      },
      plotLines: thresholds ? [
        !isNaN(thresholds.lowest) && {
          value: thresholds.lowest,
          color: '#0F08D1',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `ความจุต่ำสุด (${thresholds.lowest}) ลบ.ม / วินาที`, style: { color: '#0F08D1', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
        !isNaN(thresholds.alarm) && {
          value: thresholds.alarm,
          color: '#DFC328',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `เฝ้าระวัง (${thresholds.alarm}) ลบ.ม / วินาที`, style: { color: '#DFC328', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
        !isNaN(thresholds.warning) && {
          value: thresholds.warning,
          color: '#ED9B4D',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `เตือนภัย (${thresholds.warning}) ลบ.ม / วินาที`, style: { color: '#ED9B4D', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
        !isNaN(thresholds.critical) && {
          value: thresholds.critical,
          color: '#C83C35',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `วิกฤต (${thresholds.critical}) ลบ.ม / วินาที`, style: { color: '#C83C35', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
      ].filter(Boolean) : [],
    },
    tooltip: {
      crosshairs: true,
      shared: true,
      formatter() {
        const date = moment(this.x).locale('th').format('DD MMM HH:mm');
        let html = `<b>${date}</b>`;
        (this.points || []).forEach(point => {
          const val = point.y?.toFixed(2);
          const color = point.series.color;
          const name = point.series.name === 'ค่าตรวจวัด' ? 'ค่าตรวจวัด' : seriesName;
          html += `<br/><span style="color:${color}">●</span> ${name}: <b>${val}</b> ${unit}`;
        });
        return html;
      },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { fontFamily: 'Noto Sans Thai', fontSize: '10px', color: '#7E8F9F' },
    },
    series: [
      {
        name: 'ค่าตรวจวัด',
        type: 'areaspline', 
        lineWidth: 2,
        color: '#000000',
        fillColor: 'transparent',
        marker: {
          enabled: null,
          enabledThreshold: 2,
          symbol: 'circle',
          radius: 5,
          fillColor: '#000000',
          lineWidth: 2,
          states: { hover: { enabled: false } },
        },
        data: observedSeriesData,
        states: {
          hover: {
            enabled: false
          }
        },
        zIndex: 10,
      },
      {
        name: seriesName,
        type: 'areaspline',
        lineWidth: 2,
        color: isReservoir ? '#df5745' : '#349961',
        fillColor: isReservoir ? '#fcefed' : '#ecf5f0',
        marker: {
          enabled: null,
          enabledThreshold: 2,
          symbol: 'circle',
          radius: 5,
          fillColor: isReservoir ? '#df5745' : '#349961',
          lineWidth: 2,
          states: { hover: { enabled: false } },
        },
        data: forecastSeriesData,
        zIndex: 5,
        states: {
          hover: {
            enabled: false
          }
        },
      },
    ],
    exporting: {
      enabled: true,
      filename: `${stationName}_${moment().format('YYYYMMDD')}`,
      buttons: {
        contextButton: {
          enabled: false,
          menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
          align: 'left',
          y: -10,
          x: -10,
        },
      },
    },
    credits: { enabled: false },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      constructorType="chart"
      options={options}
      ref={ref}
    />
  );
});
export default RRIStationChart;